import { useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertTriangle, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { useApi, mediaUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { waLink } from "../lib/format";
import { Loading, PageHero, Reveal, SectionHead } from "../components/Common";
import LeadForm from "../components/LeadForm";
import InvestmentCalculator from "../components/InvestmentCalculator";
import ViewingForm from "../components/ViewingForm";
import PropertyCard from "../components/PropertyCard";
import { ProjectCard } from "../components/Cards";
import Seo from "../components/Seo";
import NotFound from "./NotFound";

function Sections({ page }) {
  const { tr, lang } = useI18n();
  return (
    <div className="space-y-px">
      {page.sections.map((s, i) => (
        <Reveal key={s.heading_en} delay={i * 0.05} className="grid scroll-mt-28 gap-6 border-t border-fg/10 py-12 md:grid-cols-[1fr_2fr]">
          <h2 id={s.anchor || undefined} data-testid={s.anchor ? `section-${s.anchor}` : undefined} className="h-display scroll-mt-28 text-3xl text-cta">{tr(s, "heading")}</h2>
          <div>
            {tr(s, "body") && <p className="text-lg leading-relaxed text-fg/75">{tr(s, "body")}</p>}
            {s[`items_${lang}`]?.length > 0 && (
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">{s[`items_${lang}`].map((it) => (<li key={it} className="glass rounded-sm px-5 py-4 font-display text-xl">{it}</li>))}</ul>
            )}
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export function ContentPage({ slug, children }) {
  const { t, tr } = useI18n();
  const { data, loading, error } = useApi(`/pages/${slug}`);
  const { hash } = useLocation();
  useEffect(() => {
    if (!data || !hash) return undefined;
    const id = setTimeout(() => document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(id);
  }, [data, hash]);
  if (loading) return <Loading />;
  if (error || !data) return <NotFound />;
  return (
    <div data-testid={`content-page-${slug}`}>
      <Seo seo={data.seo} title={tr(data, "title")} description={tr(data, "subtitle")} image={mediaUrl(data.hero_image)} />
      <PageHero eyebrow={t("nav.about")} title={tr(data, "title")} sub={tr(data, "subtitle")} image={mediaUrl(data.hero_image)} />
      <div className="container-lux py-12">
        {data.legal_review_required && (
          <p data-testid="legal-review-notice" role="note" className="mb-10 flex items-center gap-3 rounded-sm border border-flame/50 bg-flame/10 p-4 text-sm text-fg"><AlertTriangle className="h-5 w-5 shrink-0 text-flame" />{t("legal.review")}</p>
        )}
        <Sections page={data} />
        {children}
      </div>
    </div>
  );
}

export function Services() {
  const { t } = useI18n();
  return (
    <ContentPage slug="services">
      <div className="mt-12 flex flex-wrap gap-3 border-t border-fg/10 pt-12">
        <Link to="/contact" data-testid="services-contact-cta" className="btn-cta">{t("home.talk")}</Link>
        <Link to="/investment" data-testid="services-invest-cta" className="btn-ghost">{t("hero.invest")}</Link>
      </div>
    </ContentPage>
  );
}

export function Investment() {
  const { t } = useI18n();
  const props = useApi("/properties?purpose=investment&page_size=8");
  const projects = useApi("/projects?upcoming=true");
  return (
    <ContentPage slug="investment">
      <section className="py-16">
        <SectionHead title={t("invest.opportunities")} action={<Link to="/land-investment" data-testid="investment-view-all" className="text-sm text-cta">{t("home.viewAll")} →</Link>} />
        <div data-testid="investment-properties" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{props.data?.items.map((p) => (<PropertyCard key={p.id} p={p} />))}</div>
      </section>
      <section className="py-16">
        <SectionHead title={t("invest.upcoming")} />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">{projects.data?.map((p) => (<ProjectCard key={p.id} p={p} />))}</div>
      </section>
      <section className="py-16"><InvestmentCalculator /></section>
      <section id="investor-form" className="grid gap-10 border-t border-fg/10 py-16 lg:grid-cols-2">
        <SectionHead title={t("invest.formTitle")} sub={t("invest.formSub")} />
        <div className="glass rounded-sm p-6 md:p-8"><LeadForm type="investor" prefix="investor" investor /></div>
      </section>
    </ContentPage>
  );
}

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex gap-4 border-b border-fg/10 py-6">
    <Icon className="mt-1 h-5 w-5 text-cta" />
    <div><p className="text-[11px] uppercase tracking-widest text-fg/45">{label}</p><div className="mt-1 text-lg text-fg">{children}</div></div>
  </div>
);

export function Contact() {
  const { t, tr, lang } = useI18n();
  const { settings: s } = useSite();
  const ph = s?.placeholders || {};
  return (
    <div data-testid="contact-page">
      <Seo title={t("contact.title")} description={t("contact.sub")} />
      <PageHero eyebrow={t("nav.contact")} title={t("contact.title")} sub={t("contact.sub")} />
      <div className="container-lux grid gap-14 py-16 lg:grid-cols-2">
        <div>
          {s && (
            <>
              <InfoRow icon={Phone} label={t("contact.phone")}>
                <span className="flex flex-col gap-1">
                  {[[s.phone, lang === "ar" ? "المكتب الرئيسي" : "Main office"], ...(s.mobiles || []).map((m, k) => [m, k ? (lang === "ar" ? "جوال (ثانوي)" : "Mobile (secondary)") : (lang === "ar" ? "جوال المبيعات" : "Sales mobile")])].filter(([n]) => n).map(([n, l], k) => (
                    <span key={n}><a href={`tel:${n.replace(/\s/g, "")}`} dir="ltr" data-testid={k ? `contact-mobile-${k}` : "contact-phone"} className="hover:text-cta">{n}</a><span className="ms-2 text-xs text-fg/45">{l}</span></span>))}
                </span>
              </InfoRow>
              <InfoRow icon={Mail} label={t("contact.email")}>
                <ul data-testid="contact-department-emails" className="space-y-1">
                  {(s.department_emails?.length ? s.department_emails : [{ key: "general", label_en: "General", label_ar: "عام", email: s.email }]).map((d) => (
                    <li key={d.key}><span className="me-2 text-xs text-fg/45">{tr(d, "label")}</span><a href={`mailto:${d.email}`} data-testid={`contact-email-${d.key}`} className="hover:text-cta">{d.email}</a></li>))}
                </ul>
              </InfoRow>
              {(s.cr_number || s.vat_number) && <p data-testid="contact-registration" className="mt-2 text-xs text-fg/50">{s.cr_number && `${lang === "ar" ? "س.ت" : "C.R."} ${s.cr_number}`}{s.vat_number && ` · ${lang === "ar" ? "الرقم الضريبي" : "VAT No."} ${s.vat_number}`}</p>}
              {!ph.address && <InfoRow icon={MapPin} label={t("contact.address")}><span data-testid="contact-address">{tr(s, "address")}</span></InfoRow>}
              <a href={waLink(s.whatsapp, tr(s, "company_name"))} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button" className="btn-brand mt-8"><MessageCircle className="h-4 w-4" />{t("contact.whatsapp")}</a>
            </>
          )}
        </div>
        <div className="glass rounded-sm p-6 md:p-8">
          <h2 className="h-display mb-6 text-2xl">{t("contact.formTitle")}</h2>
          <LeadForm type="contact" prefix="contact" />
        </div>
      </div>
    </div>
  );
}

export function ScheduleViewing() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  return (
    <div data-testid="schedule-viewing-page">
      <Seo title={t("scheduleP.title")} description={t("scheduleP.sub")} />
      <PageHero eyebrow={t("nav.schedule")} title={t("scheduleP.title")} sub={t("scheduleP.sub")} />
      <div className="container-lux max-w-3xl py-16">
        <div className="glass rounded-sm p-6 md:p-10"><ViewingForm choosable propertyId={params.get("property") || ""} prefix="schedule" /></div>
      </div>
    </div>
  );
}
